// AI Assistant Types

export type AIProvider = 'openai' | 'anthropic' | 'custom';

export interface AISettings {
  enableAiAssistant: boolean;
  aiProvider: AIProvider | null;
  aiApiKey: string | null;
  aiApiEndpoint: string | null;
  aiModel: string | null;
}

export interface AIProviderConfig {
  id: AIProvider;
  name: string;
  defaultEndpoint: string;
  defaultModel: string;
  models: string[];
}

export const AI_PROVIDERS: AIProviderConfig[] = [
  {
    id: 'openai',
    name: 'OpenAI',
    defaultEndpoint: 'https://api.openai.com/v1',
    defaultModel: 'gpt-5',
    models: ['gpt-5', 'gpt-5-codex', 'gpt-4-1']
  },
  {
    id: 'anthropic',
    name: 'Anthropic',
    defaultEndpoint: 'https://api.anthropic.com/v1',
    defaultModel: 'claude-opus-4-1',
    models: ['claude-opus-4-1', 'claude-sonnet-4-5', 'claude-haiku-3-5']
  },
  {
    id: 'custom',
    name: 'Custom (OpenAI-compatible)',
    defaultEndpoint: '',
    defaultModel: '',
    models: []
  }
];

// Function calling types
export interface FunctionCall {
  name: string;
  arguments: string;
}

export interface ToolCall {
  id: string;
  type: 'function';
  function: FunctionCall;
}

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant' | 'tool';
  content: string;
  tool_calls?: ToolCall[];
  tool_call_id?: string;
  name?: string;
}

export interface FunctionDefinition {
  name: string;
  description: string;
  parameters: {
    type: 'object';
    properties: Record<string, any>;
    required?: string[];
  };
}

export interface Tool {
  type: 'function';
  function: FunctionDefinition;
}

// Available functions for the AI assistant
export const AI_TOOLS: Tool[] = [
  {
    type: 'function',
    function: {
      name: 'read_sketch',
      description: 'Read the current sketch code. Use this to see what the user is working on.',
      parameters: {
        type: 'object',
        properties: {},
        required: []
      }
    }
  },
  {
    type: 'function',
    function: {
      name: 'edit_sketch_code',
      description: 'Edit or replace the sketch code. Provide the full new code content.',
      parameters: {
        type: 'object',
        properties: {
          code: {
            type: 'string',
            description: 'The complete new code for the sketch'
          },
          explanation: {
            type: 'string',
            description: 'Brief explanation of what was changed and why'
          }
        },
        required: ['code', 'explanation']
      }
    }
  },
  {
    type: 'function',
    function: {
      name: 'get_sketch_errors',
      description: 'Get any compilation or runtime errors from the sketch.',
      parameters: {
        type: 'object',
        properties: {},
        required: []
      }
    }
  },
  {
    type: 'function',
    function: {
      name: 'read_controls',
      description: 'Read the current control definitions. Controls are UI elements like sliders, checkboxes, etc. that provide interactive parameters to the sketch.',
      parameters: {
        type: 'object',
        properties: {},
        required: []
      }
    }
  },
  {
    type: 'function',
    function: {
      name: 'add_control',
      description: 'Add a new control to the sketch. Controls appear in the UI and their values are available as variables in the code.',
      parameters: {
        type: 'object',
        properties: {
          control: {
            type: 'object',
            description: 'The control definition',
            properties: {
              type: {
                type: 'string',
                enum: ['slider', 'checkbox', 'text', 'color'],
                description: 'The type of control'
              },
              name: {
                type: 'string',
                description: 'The variable name (used in code). Must be valid Python identifier.'
              },
              label: {
                type: 'string',
                description: 'Display label for the control'
              },
              value: {
                description: 'Default value (number for slider, boolean for checkbox, string for text/color)'
              },
              min: {
                type: 'number',
                description: 'Minimum value (required for slider)'
              },
              max: {
                type: 'number',
                description: 'Maximum value (required for slider)'
              },
              step: {
                type: 'number',
                description: 'Step size (required for slider)'
              }
            },
            required: ['type', 'name', 'label', 'value']
          }
        },
        required: ['control']
      }
    }
  },
  {
    type: 'function',
    function: {
      name: 'update_control',
      description: 'Update an existing control definition. Use this to change properties like min/max/step or the label.',
      parameters: {
        type: 'object',
        properties: {
          name: {
            type: 'string',
            description: 'The name of the control to update'
          },
          updates: {
            type: 'object',
            description: 'Properties to update',
            properties: {
              label: { type: 'string' },
              value: { description: 'New default value' },
              min: { type: 'number' },
              max: { type: 'number' },
              step: { type: 'number' }
            }
          }
        },
        required: ['name', 'updates']
      }
    }
  },
  {
    type: 'function',
    function: {
      name: 'remove_control',
      description: 'Remove a control from the sketch.',
      parameters: {
        type: 'object',
        properties: {
          name: {
            type: 'string',
            description: 'The name of the control to remove'
          }
        },
        required: ['name']
      }
    }
  },
  {
    type: 'function',
    function: {
      name: 'set_controls',
      description: 'Replace all controls with a new set. Use this when you need to define multiple controls at once or completely restructure the controls.',
      parameters: {
        type: 'object',
        properties: {
          controls: {
            type: 'array',
            description: 'Array of control definitions',
            items: {
              type: 'object',
              properties: {
                type: {
                  type: 'string',
                  enum: ['slider', 'checkbox', 'text', 'color']
                },
                name: { type: 'string' },
                label: { type: 'string' },
                value: {},
                min: { type: 'number' },
                max: { type: 'number' },
                step: { type: 'number' }
              },
              required: ['type', 'name', 'label', 'value']
            }
          }
        },
        required: ['controls']
      }
    }
  }
];
